/**
 * Validación en el cliente de los formularios de acceso.
 *
 * Es un espejo de `backend/src/modules/auth/auth.schemas.ts`: mismas reglas y mensajes, para
 * que el usuario vea el error justo debajo del campo sin esperar al viaje al servidor (el
 * backend ya devuelve el campo de cada mensaje en `error.details`, ver `src/lib/form-errors.ts`).
 *
 * El backend sigue siendo la autoridad: si una regla cambia allí y el cliente no la conoce, su
 * mensaje se pinta igual (bajo el campo o en el aviso general del formulario).
 */

/** Longitudes tomadas de `auth.schemas.ts` (password ≥ 8, nombre ≤ 120, empresa ≤ 150). */
export const PASSWORD_MIN_LENGTH = 8;
export const NOMBRE_MAX_LENGTH = 120;
export const EMPRESA_MAX_LENGTH = 150;

/**
 * Comprobación de email deliberadamente laxa (la misma idea que usa Zod: "algo@algo.algo").
 * La validación de verdad la hace el backend; aquí solo se evita el viaje redondo obvio.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** `undefined` = sin error. */
export function validateEmail(value: string): string | undefined {
  const email = value.trim();
  if (!email) return 'Ingresa tu email';
  if (!EMAIL_PATTERN.test(email)) return 'Email inválido';
  return undefined;
}

/** Contraseña del login: solo que no esté vacía (igual que `loginSchema`). */
export function validateLoginPassword(value: string): string | undefined {
  if (!value) return 'La contraseña es obligatoria';
  return undefined;
}

/** Contraseña del registro: mínimo 8 caracteres (igual que `registerSchema`). */
export function validateNewPassword(value: string): string | undefined {
  if (!value) return 'La contraseña es obligatoria';
  if (value.length < PASSWORD_MIN_LENGTH) {
    return `La contraseña debe tener al menos ${PASSWORD_MIN_LENGTH} caracteres`;
  }
  return undefined;
}

/** Nombre de la empresa: obligatorio y con tope (igual que `registerSchema`). */
export function validateNombreEmpresa(value: string): string | undefined {
  const nombre = value.trim();
  if (!nombre) return 'El nombre de la empresa es obligatorio';
  if (nombre.length > EMPRESA_MAX_LENGTH) {
    return `El nombre de la empresa no puede superar los ${EMPRESA_MAX_LENGTH} caracteres`;
  }
  return undefined;
}

/** Nombre de la persona: opcional, pero con tope si se escribe. */
export function validateNombre(value: string): string | undefined {
  if (value.trim().length > NOMBRE_MAX_LENGTH) {
    return `El nombre no puede superar los ${NOMBRE_MAX_LENGTH} caracteres`;
  }
  return undefined;
}
